import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { server } from "../../server";
import axios from "axios";

const DownloadResultXLS = async ({
  marks,
  heads,
  attendanceData,
  selectedClass,
  selectedSection,
  workingDays,
}) => {
  marks.sort((a, b) => {
    const rollA = Number(a.rollNo) || Infinity;
    const rollB = Number(b.rollNo) || Infinity;
    return rollA - rollB;
  });

  const subjectOrder = [
    "English",
    "Hindi",
    "Maths",
    "EVS",
    "Science",
    "SST",
    "Sanskrit",
    "GK",
    "AI",
  ];

  const halfYearlyConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "halfyearly",
  );
  const headLabels = halfYearlyConfig.heads;

  const getGrade = (percentage) => {
    if (percentage >= 91) return "A1";
    if (percentage >= 81) return "A2";
    if (percentage >= 71) return "B1";
    if (percentage >= 61) return "B2";
    if (percentage >= 51) return "C1";
    if (percentage >= 41) return "C2";
    if (percentage >= 33) return "D";
    return "E";
  };

  const normalize = (str) => str?.trim().toLowerCase();

  // Visible heads: skip "weekly test 2", rename "weekly test 1" → "Weekly Test (10)"
  const visibleHeads = headLabels.filter(
    (h) => h.label.toLowerCase() !== "weekly test 2",
  );
  const headColumnLabels = visibleHeads.map((h) =>
    h.label.toLowerCase() === "weekly test 1"
      ? "Weekly Test (10)"
      : `${h.label} (${h.max})`,
  );

  // ── Fetch all students ────────────────────────────────────────────────
  const studentLookup = {};
  try {
    const bulkRes = await axios.post(
      `${server}/studentdata_using_class_and_section_mongodb`,
      { class: selectedClass, section: selectedSection },
      {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      },
    );
    const bulkData = bulkRes.data?.["body-json"]?.body?.students || [];
    bulkData.forEach((s) => {
      if (s.admission_no) studentLookup[s.admission_no] = s;
    });
  } catch (err) {
    console.error("Bulk student fetch failed:", err);
  }

  // ── Build header row ──────────────────────────────────────────────────
  const infoColumns = [
    "Roll No",
    "Admission No",
    "Student Name",
    "Father Name",
    "Mother Name",
    "Class",
    "Section",
    "DOB",
    "Days Present",
    "Working Days",
    "Attendance %",
  ];

  // Per-subject: each head column + Subject Total (100) + Grade
  const subjectColumns = [];
  subjectOrder.forEach((subject) => {
    headColumnLabels.forEach((headLabel) => {
      subjectColumns.push(`${subject} - ${headLabel}`);
    });
    subjectColumns.push(`${subject} - Total (100)`);
    subjectColumns.push(`${subject} - Grade`);
  });

  const summaryColumns = [
    "Grand Total Obtained",
    "Grand Total Max",
    "Percentage (%)",
    "Overall Grade",
  ];

  const headerRow = [...infoColumns, ...subjectColumns, ...summaryColumns];

  // ── Build data rows ───────────────────────────────────────────────────
  const dataRows = [];

  for (const student of marks) {
    const enriched = studentLookup[student.admission_no]
      ? { ...student, ...studentLookup[student.admission_no] }
      : student;

    const studentName = enriched?.name || "-";
    const fatherName = enriched?.parentDetails?.[0]?.name || "-";
    const motherName = enriched?.parentDetails?.[1]?.name || "-";
    const currentClass = enriched?.currentClass || "-";
    const section = enriched?.studentDetails?.currentSection || "-";
    const rollNo = enriched?.studentDetails?.rollNo || "-";
    const dobRaw = enriched?.dateOfBirth || "-";
    const dob = dobRaw !== "-" ? dobRaw.split("-").reverse().join("/") : "-";

    // Attendance
    const studentAttendance =
      attendanceData.find((att) => att.admission_no === enriched.admission_no)
        ?.attendance || [];

    const totalPresentDays = studentAttendance.reduce(
      (count, day) =>
        count + (day.SchoolStatus?.toLowerCase() === "present" ? 1 : 0),
      0,
    );

    const attendancePct =
      workingDays > 0
        ? parseFloat(((totalPresentDays / workingDays) * 100).toFixed(2))
        : 0;

    // Subjects
    const studentSubjects = enriched.result?.["half yearly"] || [];
    const subjectMap = {};
    studentSubjects.forEach((sub) => {
      subjectMap[normalize(sub.subject)] = sub;
    });

    let grandObtained = 0;
    let grandMax = 0;
    const subjectCells = [];

    subjectOrder.forEach((subjectName) => {
      const subjectObj = subjectMap[normalize(subjectName)];
      const marksByHead = subjectObj?.marks || {};

      let subjectObtained = 0;
      let subjectMax = 0;

      visibleHeads.forEach((h) => {
        if (h.label.toLowerCase() === "weekly test 1") {
          const s1 = Number(marksByHead["h1"]) || 0;
          const s2 = Number(marksByHead["h2"]) || 0;
          const nullBoth =
            (marksByHead["h1"] === null || marksByHead["h1"] === 0) &&
            (marksByHead["h2"] === null || marksByHead["h2"] === 0);

          subjectMax += 10;
          grandMax += 10;

          if (!nullBoth) {
            const combined = Math.round((s1 + s2) / 4);
            subjectObtained += combined;
            grandObtained += combined;
            subjectCells.push(combined);
          } else {
            subjectCells.push(subjectObj ? "AB" : "-");
          }
        } else {
          const score = marksByHead[h.id] ?? null;
          subjectMax += h.max;
          grandMax += h.max;

          if (!subjectObj) {
            subjectCells.push("-");
          } else if (score === null || score === 0) {
            subjectCells.push("AB");
          } else {
            subjectObtained += score;
            grandObtained += score;
            subjectCells.push(score);
          }
        }
      });

      // Subject total + grade (push after all head cells for this subject)
      const subjectPct =
        subjectMax > 0 ? (subjectObtained / subjectMax) * 100 : 0;
      subjectCells.push(subjectObj ? subjectObtained : "-");
      subjectCells.push(subjectObj ? getGrade(subjectPct) : "-");
    });

    const percentage =
      grandMax > 0
        ? parseFloat(((grandObtained / grandMax) * 100).toFixed(2))
        : 0;
    const overallGrade = getGrade(percentage);

    dataRows.push([
      rollNo,
      enriched.admission_no,
      studentName,
      fatherName,
      motherName,
      currentClass,
      section,
      dob,
      totalPresentDays,
      workingDays,
      attendancePct,
      ...subjectCells,
      grandObtained,
      grandMax,
      percentage,
      overallGrade,
    ]);
  }

  // ── Workbook ──────────────────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  ws["!cols"] = headerRow.map((h) => ({
    wch: Math.max(String(h).length + 2, 12),
  }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  XLSX.utils.book_append_sheet(wb, ws, "Half Yearly Result");

  // Grade Scale
  const gradeSheet = XLSX.utils.aoa_to_sheet([
    ["Marks Range", "Grade"],
    ["91 - 100", "A1"],
    ["81 - 90", "A2"],
    ["71 - 80", "B1"],
    ["61 - 70", "B2"],
    ["51 - 60", "C1"],
    ["41 - 50", "C2"],
    ["33 - 40", "D"],
    ["32 & Below", "E"],
  ]);
  gradeSheet["!cols"] = [{ wch: 15 }, { wch: 8 }];
  XLSX.utils.book_append_sheet(wb, gradeSheet, "Grade Scale");

  const xlsxBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([xlsxBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(
    blob,
    `${selectedClass}-${selectedSection}-HalfYearly-Result-2025.xlsx`,
  );
};

export default DownloadResultXLS;
